import AddEditLoader from "components/common/detailsPage/AddEditLoader";
import CustomField from "components/form/CustomField";
import NoPermission from "components/common/NoPermission";
import PageHeader from "components/common/PageHeader";
import QRCodeCard from "components/common/QRCodeCard";
import { Form, Formik } from "formik";
import { handleSubmitForm } from "helpers/apiUtils";
import { usePermission } from "hooks/usePermission";
import { useQueryParam } from "hooks/useQueryParam";
import { useSmartBack } from "hooks/useSmartBack";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  addQrLink,
  getQrLinkDetails,
  updateQrLink,
} from "store/slices/qrLinksSlice";
import { getQrLinkValidationSchema } from "utils/validations";
import { Images } from "assets/images";

const AddEditQRLink = ({ mode }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const id = useQueryParam("id");
  const isEdit = mode === "edit";
  const { canAction } = usePermission();
  const { canAdd, canEdit } = canAction("qr_links");
  const { isLoading, loading, itemDetails } = useSelector(
    (state) => state.QRLinks,
  );
  const { logo } = Images || {};
  const [initialValues, setInitialValues] = useState({
    label: "",
    fallbackUrl: "",
    dynamicParams: { locationName: "" },
  });

  useEffect(() => {
    if (isEdit && id && canEdit) dispatch(getQrLinkDetails(id));
  }, [isEdit, id, canEdit, dispatch]);

  useEffect(() => {
    if (isEdit && itemDetails) {
      setInitialValues({
        label: itemDetails.label || "",
        fallbackUrl: itemDetails.fallbackUrl || "",
        dynamicParams: {
          locationName: itemDetails.dynamicParams?.locationName || "",
        },
      });
    }
  }, [isEdit, itemDetails]);

  const handleSubmit = (values) => {
    const payload = isEdit ? { ...values, id } : values;
    handleSubmitForm({
      dispatch,
      action: isEdit ? updateQrLink : addQrLink,
      payload,
      onSuccess: () => navigate("/qr-links"),
    });
  };

  if ((!isEdit && !canAdd) || (isEdit && !canEdit)) return <NoPermission />;
  if (isEdit && loading) return <AddEditLoader title={t("Edit QR Link")} />;

  const qrValue =
    process.env.REACT_APP_QR_CODE_LINK && itemDetails?.identifierCode
      ? `${process.env.REACT_APP_QR_CODE_LINK}${itemDetails.identifierCode}`
      : "";

  return (
    <div className="page-content">
      <PageHeader
        isBackBtn
        title={isEdit ? t("Edit QR Link") : t("Add QR Link")}
      />
      <div className="common_card">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={getQrLinkValidationSchema(t)}
          onSubmit={handleSubmit}
        >
          <Form>
            <Row>
              <Col lg={6}>
                <div className="form_field">
                  <CustomField
                    label={t("Label")}
                    name="label"
                    placeholder={t("Enter internal QR label")}
                    required
                  />
                </div>
              </Col>
              <Col lg={6}>
                <div className="form_field">
                  <CustomField
                    label={t("Fallback URL")}
                    name="fallbackUrl"
                    placeholder="https://example.com/wash"
                    required
                  />
                </div>
              </Col>
              <Col lg={6}>
                <div className="form_field">
                  <CustomField
                    label={t("Location Name")}
                    name="dynamicParams.locationName"
                    placeholder={t("Enter location name")}
                    required
                  />
                </div>
              </Col>
              <Col lg={12} className="d-flex justify-content-end gap-2 mt-5">
                <button
                  type="button"
                  className="main_btn outline"
                  onClick={goBack}
                >
                  {t("Cancel")}
                </button>
                <button type="submit" className="main_btn" disabled={isLoading}>
                  {isEdit ? t("Save Changes") : t("Add QR Link")}
                </button>
              </Col>
            </Row>
          </Form>
        </Formik>
      </div>
      {qrValue && (
        <div className="common_card">
          <h3 className="page_title">{t("QR Code")}</h3>
          <QRCodeCard
            value={qrValue}
            fileName={itemDetails?.label || "qr-code"}
            image={logo}
          />
        </div>
      )}
    </div>
  );
};

export default AddEditQRLink;
